import { useEffect } from "react";

import { useIsRestoring, useQueryClient } from "@tanstack/react-query";

import { getAdminAreaHierarchy } from "@/api/adminArea/adminAreaApi";
import { getCountries } from "@/api/country/countryApi";
import { CountryItem } from "@/types/country";

// Matches the persister maxAge (src/lib/react-query.ts)
const ONE_DAY_MS = 1000 * 60 * 60 * 24;

/**
 * Background-prefetches the country list and per-country admin-area
 * hierarchies once per authenticated session, so the UI that needs them
 * (POI tab, admin area settings) renders instantly instead of waiting ~5s.
 *
 * Query keys and queryFn result shapes MUST mirror useGetCountries and
 * useGetAdminAreaHierarchy so the cache is shared with those consumers.
 * Everything fails silently — consumers fetch on demand as fallback.
 */
export const usePrefetchAdminAreaData = (enabled: boolean) => {
  const queryClient = useQueryClient();
  // Wait for PersistQueryClientProvider to finish restoring from IndexedDB;
  // unlike useQuery, fetchQuery does not respect the restore phase on its own
  const isRestoring = useIsRestoring();

  useEffect(() => {
    if (!enabled || isRestoring) return;

    const prefetch = async () => {
      // Warm the unfiltered list used by most consumers (fire-and-forget;
      // prefetchQuery swallows errors)
      void queryClient.prefetchQuery({
        queryKey: ["countries", undefined],
        queryFn: () => getCountries(),
        staleTime: ONE_DAY_MS,
        gcTime: ONE_DAY_MS,
        meta: { shouldPersist: true }
      });

      let subscribedCountries: CountryItem[];
      try {
        subscribedCountries = await queryClient.fetchQuery({
          queryKey: ["countries", true],
          queryFn: () => getCountries({ subscribed: true }),
          staleTime: ONE_DAY_MS,
          gcTime: ONE_DAY_MS,
          meta: { shouldPersist: true }
        });
      } catch {
        // Silent background failure — consumers refetch on demand
        return;
      }

      // Exclude the "Global" pseudo-country (same rule as POIParameterTab)
      const targets = (subscribedCountries ?? []).filter(
        (country) => country.name.toLowerCase() !== "global"
      );

      await Promise.allSettled(
        targets.map((country) =>
          queryClient.prefetchQuery({
            queryKey: ["admin-area-hierarchy", country.id],
            queryFn: async () => {
              const response = await getAdminAreaHierarchy(country.id);
              // Must match the useGetAdminAreaHierarchy queryFn result shape
              return response ?? null;
            },
            staleTime: ONE_DAY_MS,
            gcTime: ONE_DAY_MS,
            meta: { shouldPersist: true }
          })
        )
      );
    };

    void prefetch();
  }, [enabled, isRestoring, queryClient]);
};
